import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "../lib/utils";

/**
 * Switch — a two-state on/off control. Growth Fusion theme.
 *
 * Use for a setting that takes effect immediately ("Available on order
 * links"). For a value that only applies on Save, use a checkbox instead —
 * a switch that needs saving misleads, because it reads as already-applied.
 *
 * Accessibility contract (this is why a styled checkbox will not do):
 *   - `role="switch"` + `aria-checked`, so a screen reader announces
 *     "on"/"off" rather than "checked"/"unchecked".
 *   - The accessible name MUST come from `aria-labelledby` (pointing at the
 *     visible label's id) or `aria-label`. A `<label htmlFor>` does NOT name
 *     a button — `for`/`htmlFor` only associates with form controls, so a
 *     switch labelled that way is announced as an unnamed button. Testing
 *     Library's getByRole is more forgiving than real assistive tech here;
 *     axe is not, and axe is right. Always pair the visible <Label> with
 *     `aria-labelledby`.
 *   - `aria-describedby` is for the CONSEQUENCE ("Customers can see and
 *     order this product"), which a sighted user infers from context and a
 *     screen reader user cannot.
 *   - Space and Enter both toggle (native <button> gives Space and Enter;
 *     an <input type=checkbox> would not fire on Enter).
 *   - Focus ring is never removed — `focus-visible:ring-2` per L7 §7.9.
 *   - Disabled uses `aria-disabled` and stays focusable, so a keyboard user
 *     can reach it and discover WHY it is unavailable. A hard `disabled`
 *     attribute makes the control invisible to assistive tech.
 *
 * State is always controlled: pass `checked` and `onCheckedChange`. There is
 * no uncontrolled mode on purpose — an availability toggle that drifts out of
 * sync with the server is a correctness bug, not a UI convenience.
 */
export interface SwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onChange" | "type"> {
  /** Current state. Controlled — always supply it. */
  checked: boolean;
  /** Fired with the NEXT state. Not called while disabled. */
  onCheckedChange?: (next: boolean) => void;
  /**
   * Non-interactive. Rendered with aria-disabled (still focusable) rather
   * than the disabled attribute, so it stays discoverable by keyboard.
   */
  disabled?: boolean;
  /** Stable testid. Required by testid_guard on interactive elements. */
  "data-testid"?: string;
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  function Switch(
    { checked, onCheckedChange, disabled = false, className, onClick, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled || undefined}
        className={cn(
          "relative inline-flex h-6 w-11 shrink-0 items-center rounded-[var(--radius-full)]",
          "border transition-colors duration-150",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          "focus-visible:ring-[var(--color-brand)] focus-visible:ring-offset-transparent",
          checked
            ? "bg-[var(--color-brand)] border-[var(--color-brand)]"
            : "bg-[var(--color-surface-soft)] border-[var(--color-border-hairline)]",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          className,
        )}
        onClick={(event) => {
          onClick?.(event);
          if (disabled || event.defaultPrevented) return;
          onCheckedChange?.(!checked);
        }}
        {...rest}
      >
        {/* Decorative: the state is carried by aria-checked, not by the knob. */}
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none block h-5 w-5 rounded-[var(--radius-full)] bg-white",
            "shadow-[var(--shadow-card)] transition-transform duration-150",
            checked ? "translate-x-[22px]" : "translate-x-[2px]",
          )}
        />
      </button>
    );
  },
);
